import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { MenuFavoriteStar } from '@/components/MenuFavoriteStar';
import { MenuIcon } from '@/components/MenuIcon';
import { NewBadge } from '@/components/NewBadge';
import { iconNameOf } from '@/lib/menu/icons';
import { type MenuConfigItem } from '@/lib/menu/menuConfig';
import { tabIconOf } from '@/lib/menu/tabIcons';
import { useMenuFavoriteActions, useMenuFavorites } from '@/lib/menu/useMenuFavorites';
import { landingHrefOf, useMenuSection } from '@/lib/menu/useMenuSection';
import { useMenuTree } from '@/lib/menu/useMenuTree';

/**
 * 화면 맨 위의 <b>묶음 탭</b> — 사이드바에서 펼치던 하위 항목이 여기로 올라왔다
 * (사용자 지시 2026-09-22).
 *
 * <pre>
 *   예전   사이드바 [게시판 ▾] → 펼치면 아래로 네 줄
 *   지금   사이드바 [게시판]   → 누르면 게시판으로 가고, 그 넷은 화면 맨 위에 탭으로
 * </pre>
 *
 * <h2>두 줄은 <b>같은 옷을 입지 않는다</b> (사용자 지적 2026-09-29)</h2>
 * 그 전에는 두 줄 다 알약 세그먼트였고, 차이는 <b>크기 하나</b>였다(13px 대 12px). 그래서
 * 「둘째 줄이 첫 줄의 어느 칸에 속한 것인가」가 화면에 없었다 — 나란히 놓인 두 덩어리로 읽혔다.
 *
 * <p>지금은 셋으로 갈라 말한다.
 * <ul>
 *   <li><b>그림</b> — 첫 줄만 아이콘을 단다({@link tabIconOf}). 있고 없음은 크기 차이보다 먼저 읽힌다.
 *   <li><b>색</b> — 첫 줄의 고른 칸은 <b>진한 남색 바탕</b>(`--accent`)이고, 둘째 줄은 그 색의
 *       옅은 바탕(`--accent-bg`) 위에 앉는다. 같은 색 계열의 진하고 옅음이 곧 위아래다.
 *   <li><b>되풀이</b> — 둘째 줄 왼쪽 끝에 <b>고른 첫 줄 칸의 그림</b>을 한 번 더 놓는다.
 *       탭이 접혀 여러 줄이 되어도(환경설정은 열넷이다) 「이 줄은 저것의 속」이 흔들리지 않는다.
 * </ul>
 *
 * <p>세로선·꺾쇠로 잇는 방법도 있는데 쓰지 않았다. 고른 칸이 가로로 어디 서 있는지를 재서 그
 * 아래에 선을 놓아야 하고, 줄이 접히는 순간(환경설정·인사자료) 그 선이 남의 줄을 가로지른다.
 *
 * <h2>이 줄이 <b>메뉴</b>라는 것을 말한다 (사용자 지적 2026-09-29)</h2>
 * 그림이 붙고 나서도 「여기가 메뉴 전환 탭인지 모르겠다」 는 말이 남았다. 칸만 예뻐졌지, 그 칸들이
 * <b>화면 속의 무엇</b>인지 — 걸러내는 단추인지 옮겨 가는 길인지 — 는 여전히 눌러 봐야 알았다.
 *
 * <p>두 가지로 말한다.
 * <ul>
 *   <li><b>왼쪽에 지금 들어와 있는 메뉴 이름</b> — 사이드바에서 누른 그 줄(인사관리·환경설정)의
 *       그림과 이름을 세우고 세로선으로 끊는다. 「인사관리 │ 인력배치표 인사자료 …」 는 읽는
 *       순서대로 «어디에 있고 그 안에 무엇이 있는가» 가 된다. 사이드바를 접었거나 모바일이면
 *       그 이름이 화면 어디에도 없었다 — 그때는 이 줄이 유일한 표지다.
 *   <li><b>띠 아래 전폭 구분선</b> — 머리줄이 쓰는 것과 같은 선이다. 선 하나가 위아래를 «화면의
 *       테두리» 와 «본문» 으로 갈라, 이 칸들이 본문의 단추가 아니라 <b>틀의 일부</b>로 읽힌다.
 * </ul>
 *
 * <p>바탕색은 여전히 깔지 않는다 — 한때 하늘색 띠였다가 「공간을 과하게 차지하고 안 예쁘다」로
 * 걷어낸 자리다(2026-09-22). 선 하나면 층은 갈리고 자리는 안 먹는다.
 *
 * <h2>칸마다 별이 붙는다 (사용자 지시 2026-09-30)</h2>
 * 즐겨찾기 별이 사이드바에만 있던 동안 담을 수 있는 것은 <b>맨 윗줄뿐</b>이었다 — 하위가 이 줄로
 * 올라온 뒤로도 별은 따라오지 않았다. 정작 매일 여는 자리는 「인사관리 › 인사자료 › 연차 촉진」
 * 처럼 두세 단 아래인데, 그것을 담을 손이 화면에 없었던 것이다({@link MenuFavoriteStar}).
 *
 * <p>별은 <b>옅게 늘 서 있다</b>. 사이드바처럼 마우스를 올린 칸에만 세우면 패드에서는 영영
 * 나타나지 않고(hover 가 없다), 칸 폭이 눌릴 때마다 바뀌어 탭 줄이 다시 접힌다.
 *
 * <h2>화면의 제 탭과 겹치지 않는다</h2>
 * 현장 조회·인사자료처럼 <b>한 화면 안에서</b> 탭을 쓰는 곳이 있다. 그쪽은 화면 자신의 칸이고
 * 이것은 <b>메뉴</b>의 칸이다 — 여기 서는 것은 사이드바에 줄이 있던 항목뿐이라 겹치지 않는다.
 *
 * <h2>화면의 버튼은 여기 두지 않는다</h2>
 * 한때 「인쇄」·「등록」 을 이 줄 오른쪽에 붙였다가 걷었다(사용자 지적 2026-09-22). 이 줄은
 * «어디로 갈까» 이고 그 버튼들은 «여기서 무엇을 할까» 다 — 자리가 남는다고 섞을 것이 아니다.
 *
 * <h2>칸에도 New 가 붙는다 (사용자 지시 2026-10-02)</h2>
 * 하위가 사이드바에서 이 줄로 올라온 뒤로, 사이드바의 「게시판」 한 줄에 New 가 뭉쳐 보일 뿐
 * <b>그 안의 어느 화면</b>이 새 것인지는 어디에도 없었다. 사이드바와 같은 판정({@code hasBadge})을
 * 칸마다 단다 — 그룹 줄이 자식에게서 물려받는 그 값을 자식 자리에서 그대로 보이는 것이다.
 */
export function MenuTabs() {
  const { childrenOf, hasBadge } = useMenuTree();
  const { top, tabs, activeTab, subTabs, activeSubTab } = useMenuSection();
  // 사이드바와 같은 목록이다 — 캐시가 하나라 여기서 담으면 왼쪽 즐겨찾기 칸이 곧바로 따라 선다.
  const { data: favoriteKeys = [] } = useMenuFavorites();
  const favoriteActions = useMenuFavoriteActions();
  const favoriteSet = useMemo(() => new Set(favoriteKeys), [favoriteKeys]);
  const toggleFavorite = (menuKey: string) =>
    favoriteActions.toggle.mutate({ menuKey, favorite: !favoriteSet.has(menuKey) });

  if (tabs.length === 0) return null;

  const parentIcon = activeTab ? tabIconOf(activeTab.menuKey) : null;
  const topHref = top ? landingHrefOf(top, childrenOf) : null;

  return (
    <nav
      aria-label="메뉴"
      className="no-print"
      style={{ padding: '9px 0 9px', borderBottom: '1px solid var(--border)' }}
    >
      {/*
        첫 줄 — 지금 들어와 있는 메뉴 이름, 세로선, 그리고 그 안의 칸들. 칸이 많아 한 줄에 안
        들어가면 접힌다({@code flex-wrap}). 가로로 흘려 스크롤하게 두지 않는다 — 사이드바를
        접지 않은 좁은 화면에서 뒤쪽 탭이 화면 밖에 숨는 것이 가장 나쁘다.
      */}
      <div className="flex flex-wrap items-center px-4 md:px-6" style={{ gap: '4px' }}>
        {top && (
          <SectionLabel item={top} href={topHref} />
        )}
        {tabs.map((item) => (
          <TabLink
            key={item.menuKey}
            item={item}
            active={item.menuKey === activeTab?.menuKey}
            badge={hasBadge(item.menuKey)}
            childrenOf={childrenOf}
            favorite={favoriteSet.has(item.menuKey)}
            onToggleFavorite={() => toggleFavorite(item.menuKey)}
          />
        ))}
      </div>

      {subTabs.length > 0 && (
        <div className="px-4 md:px-6" style={{ marginTop: '5px' }}>
        <div
          className="inline-flex flex-wrap items-center"
          style={{
            gap: '2px',
            padding: '3px 8px 3px 6px',
            borderRadius: '8px',
            border: '1px solid #dbeafe',
            background: 'var(--accent-bg)',
          }}
        >
          {/*
            위에서 고른 칸의 그림을 한 번 더. 「이 옅은 줄은 저 진한 칸의 속」이라는 말을
            자리가 아니라 <b>같은 그림</b>으로 한다 — 탭이 접혀도 안 어긋난다.
          */}
          {parentIcon && (
            <span
              aria-hidden
              className="inline-flex items-center"
              style={{ color: 'var(--accent)', opacity: 0.75, paddingRight: '6px', marginRight: '4px', borderRight: '1px solid #dbeafe' }}
            >
              <MenuIcon name={parentIcon} size={13} />
            </span>
          )}
          {subTabs.map((item) => (
            <SubTabLink
              key={item.menuKey}
              item={item}
              active={item.menuKey === activeSubTab?.menuKey}
              badge={hasBadge(item.menuKey)}
              childrenOf={childrenOf}
              favorite={favoriteSet.has(item.menuKey)}
              onToggleFavorite={() => toggleFavorite(item.menuKey)}
            />
          ))}
        </div>
        </div>
      )}
    </nav>
  );
}

/**
 * 줄 맨 왼쪽의 <b>지금 들어와 있는 메뉴</b> — 사이드바에서 누른 그 줄이다 (사용자 지적 2026-09-29
 * 「여기가 메뉴 전환 탭인지 모를 것 같다」).
 *
 * <p>탭처럼 생기지 않게 둔다 — 그림은 옅게, 글씨는 진하고 굵게, 바탕은 없고, 끝에 세로선. 이것도
 * 알약이면 <b>여섯 번째 탭</b>으로 보여, 고르는 칸이 하나 늘어난 것이 된다. 여기서 말하려는 것은
 * 「고르라」가 아니라 <b>「지금 여기」</b> 다.
 *
 * <p>그래도 <b>누르면 간다</b>. 묶음의 첫 화면으로 돌아가는 길인데, 지금은 사이드바를 접으면 그
 * 길이 화면에 없다 — 보이는데 안 눌리는 이름은 그 자체로 한 번 헛손질을 만든다.
 */
function SectionLabel({ item, href }: { item: MenuConfigItem; href: string | null }) {
  const body = (
    <>
      <span aria-hidden className="inline-flex items-center" style={{ color: 'var(--accent)', opacity: 0.8 }}>
        <MenuIcon name={iconNameOf(item.menuKey)} size={15} />
      </span>
      {item.label}
    </>
  );

  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '6px 12px 6px 2px',
    marginRight: '6px',
    borderRight: '1px solid var(--border)',
    fontSize: '13px',
    lineHeight: 1.5,
    fontWeight: 700,
    color: 'var(--text-primary)',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  } as const;

  if (!href) return <span style={style}>{body}</span>;
  return <Link to={href} style={style}>{body}</Link>;
}

/**
 * 첫 줄의 칸 — <b>그림 + 글씨</b>.
 *
 * <p>고른 칸은 진한 남색을 <b>칠한다</b>. 그 전에는 회색 덩어리 위의 흰 알약이었는데, 흰 바탕은
 * 본문 카드와 같은 색이라 「떠올랐다」는 것이 그림자 한 겹으로만 남았다 — 그것이 사용자가 말한
 * 「색상이 직관적이지 않다」다. 칠한 칸은 한 눈에 하나만 보인다.
 */
function TabLink({
  item,
  active,
  badge,
  childrenOf,
  favorite,
  onToggleFavorite,
}: {
  item: MenuConfigItem;
  active: boolean;
  /** 이 칸(과 그 아래)에 최근 새 것이 있다 — 사이드바와 같은 판정이다. */
  badge: boolean;
  childrenOf: (menuKey: string) => MenuConfigItem[];
  favorite: boolean;
  onToggleFavorite: () => void;
}) {
  const href = landingHrefOf(item, childrenOf);
  if (!href) return null;

  const icon = tabIconOf(item.menuKey);
  /*
    <b>칠하는 것은 알약(바깥 span)이고, 링크는 그 속에서 투명하다</b> (사용자 지적 2026-09-30
    「별표도 해당 메뉴를 선택한 하이라이트 안에 포함되어야지」).

    <p>별을 알약 밖에 세웠더니 고른 칸에서 <b>그 칸에 딸린 것으로 안 읽혔다</b> — 남색 알약이
    글씨에서 끊기고, 금색 별만 흰 바닥에 혼자 떠 있었다. 그래서 색·테두리·둥근 모서리를 한 겹
    바깥으로 올리고 안쪽 둘(링크·별)은 색을 <b>물려받는다</b>(`color: inherit`).

    <p>`.menutab` 클래스와 `data-active` 도 함께 올라간다 — 손 얹은 바탕을 그 규칙이 칠하므로
    (index.css §menutab) 안쪽 링크에 남겨 두면 별 밑만 안 칠해진다.
  */
  const pillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '8px',
    border: '1px solid transparent',
    background: active ? 'var(--accent)' : 'transparent',
    color: active ? '#fff' : 'var(--text-secondary)',
    fontWeight: active ? 600 : 500,
  } as const;

  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    // 오른쪽은 별이 대신 채운다 — 예전 12px 자리에 별(4px 패딩 + 12px 그림)이 들어온다.
    padding: icon ? '6px 4px 6px 10px' : '6px 4px 6px 12px',
    fontSize: '13px',
    lineHeight: 1.5,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    color: 'inherit',
    fontWeight: 'inherit',
  } as const;

  // 그림은 고르지 않은 칸에서 한 겹 물러선다 — 글씨보다 앞서 읽히면 줄 전체가 그림밭이 된다.
  const body = (
    <>
      {icon && (
        <span aria-hidden className="inline-flex items-center" style={{ opacity: active ? 1 : 0.6 }}>
          <MenuIcon name={icon} size={15} />
        </span>
      )}
      {item.label}
      {badge && <NewBadge />}
    </>
  );

  // 바깥으로 나가는 줄(휴무 계획서)은 새 창이다. 모양은 같되 이 앱을 떠난다는 것은 같다.
  const link =
    item.itemType === 'external' ? (
      <a href={href} target="_blank" rel="noreferrer" style={style}>
        {body}
      </a>
    ) : (
      <Link to={href} aria-current={active ? 'page' : undefined} style={style}>
        {body}
      </Link>
    );

  return (
    <span
      className="menutab inline-flex items-center"
      data-active={active}
      data-tour={`menutab-${item.menuKey}`}
      style={pillStyle}
    >
      {link}
      {/*
        별은 알약 <b>속</b>이다. 고른 칸의 남색 위에서는 금색도 잘 보이고, 담지 않은 별은 흰빛을
        옅게 써야 한다 — 회색(`--text-muted`)이면 남색에 묻혀 <b>있는지조차 모른다</b>.
        손 얹은 바탕도 흰빛 한 겹이다(밝은 회색 동그라미는 남색에 구멍을 낸다).
      */}
      <MenuFavoriteStar
        on={favorite}
        label={item.label}
        onToggle={onToggleFavorite}
        size={12}
        className={`${active ? 'hover:bg-white/20' : 'hover:bg-slate-200/70'} ${favorite ? '' : 'opacity-60 hover:opacity-100'}`}
        style={{
          padding: '4px',
          marginRight: '4px',
          color: favorite ? (active ? '#fbbf24' : '#f59e0b') : 'currentColor',
        }}
      />
    </span>
  );
}

/**
 * 둘째 줄의 칸 — <b>글씨만</b>, 한 겹 작게. 고른 것은 흰 바닥에 남색 글씨라, 위층의 «칠한 칸» 과
 * 안팎이 뒤집힌 관계로 읽힌다(진한 것 속의 옅은 판, 그 판 위의 흰 칸).
 */
function SubTabLink({
  item,
  active,
  badge,
  childrenOf,
  favorite,
  onToggleFavorite,
}: {
  item: MenuConfigItem;
  active: boolean;
  badge: boolean;
  childrenOf: (menuKey: string) => MenuConfigItem[];
  favorite: boolean;
  onToggleFavorite: () => void;
}) {
  const href = landingHrefOf(item, childrenOf);
  if (!href) return null;

  // 첫 줄과 같은 규칙 — <b>칠하는 것은 바깥 span</b>이고 별도 그 안에 든다(위 `pillStyle` 머리말).
  const pillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '6px',
    background: active ? '#fff' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--text-secondary)',
    fontWeight: active ? 700 : 500,
    boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
  } as const;

  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '3px 3px 3px 10px',
    fontSize: '12px',
    lineHeight: 1.5,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    color: 'inherit',
    fontWeight: 'inherit',
  } as const;

  const body = (
    <>
      {item.label}
      {badge && <NewBadge />}
    </>
  );

  const link =
    item.itemType === 'external' ? (
      <a href={href} target="_blank" rel="noreferrer" style={style}>
        {body}
      </a>
    ) : (
      <Link to={href} aria-current={active ? 'page' : undefined} style={style}>
        {body}
      </Link>
    );

  // 「인사관리 › 인사자료 › 연차 촉진」의 <b>맨 끝 단</b>이 여기다 — 담을 손이 가장 필요한 줄.
  return (
    <span
      className="menusubtab inline-flex items-center"
      data-active={active}
      data-tour={`menutab-${item.menuKey}`}
      style={pillStyle}
    >
      {link}
      <MenuFavoriteStar
        on={favorite}
        label={item.label}
        onToggle={onToggleFavorite}
        size={11}
        className={`hover:bg-slate-200/60 ${favorite ? '' : 'opacity-60 hover:opacity-100'}`}
        style={{ padding: '3px', marginRight: '3px', color: favorite ? '#f59e0b' : 'currentColor' }}
      />
    </span>
  );
}
